/**
 * A picker row's project, drawn through the shared ladder.
 *
 * Extracted from `composerChips.tsx` so the identity rules own their imports
 * instead of growing a file the size gate had already capped.
 *
 * The summary carries CMMD's identity when CMMD owns one and the checkout path
 * otherwise, which is exactly the pair `ProjectIcon` needs — the chip used to
 * draw a folder or a cloud glyph for every project alike.
 *
 * A summary without identity fields is not the end of the ladder. A connected
 * checkout linked to a CMMD project has an identity behind the link — on a
 * shared cloud runtime the catalog rows carry no resolved ref, so the summary
 * match never fires and the row used to settle for a letter avatar while the
 * sidebar, reading the same link join, showed the real mark. Same resolver,
 * therefore same answer: the summary's own identity wins, then the link join,
 * then the checkout's favicon.
 */
import type { EnvironmentId } from "@cmmd-forge/contracts";

import { ProjectIcon } from "../ProjectIcon";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import type { ProjectSummary } from "./NewThreadLanding.logic";

export function ProjectSummaryIcon({
  project,
  className,
}: {
  readonly project: ProjectSummary;
  readonly className?: string | undefined;
}) {
  const resolveProjectIcon = useProjectIconResolver();
  const canonicalIdentity =
    project.icon || project.iconUrl
      ? { icon: project.icon ?? null, iconUrl: project.iconUrl ?? null }
      : resolveProjectIcon(project.environmentId, project.resolvedProjectId ?? project.id);
  return (
    <ProjectIcon
      environmentId={project.environmentId as EnvironmentId}
      cwd={project.cwd ?? ""}
      projectName={project.name}
      canonicalIdentity={canonicalIdentity}
      className={className}
    />
  );
}
